/** Formatting shared by the Settings pages (Figma 09 wording: "Mon 14 Sep", "Today at 09:12"). */

export function initials(nameOrEmail: string | null | undefined): string {
  const source = (nameOrEmail ?? "").trim();
  if (!source) return "?";
  const base = source.includes("@") && !source.includes(" ") ? source.split("@")[0] : source;
  const words = base.split(/[\s._-]+/).filter(Boolean);
  if (words.length > 1) return (words[0][0] + words[1][0]).toUpperCase();
  return base.slice(0, 2).toUpperCase();
}

function parse(iso: string | null | undefined): Date | null {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : date;
}

function sameDay(a: Date, b: Date): boolean {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

const WEEKDAYS = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "Mon 14 Sep" (the year only when it is not this year). */
export function formatDay(iso: string | null | undefined): string {
  const date = parse(iso);
  if (!date) return "";
  const base = `${WEEKDAYS[date.getDay()]} ${date.getDate()} ${MONTHS[date.getMonth()]}`;
  return date.getFullYear() === new Date().getFullYear() ? base : `${base} ${date.getFullYear()}`;
}

export function formatTime(date: Date): string {
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" });
}

/** "Today at 09:12", "Yesterday at 16:40", else "Mon 14 Sep, 09:12". */
export function formatMoment(iso: string | null | undefined): string {
  const date = parse(iso);
  if (!date) return "";
  const now = new Date();
  if (sameDay(date, now)) return `Today at ${formatTime(date)}`;
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return `Yesterday at ${formatTime(date)}`;
  return `${formatDay(iso)}, ${formatTime(date)}`;
}

/** "used today" / "used yesterday" / "used Mon 14 Sep" / "never used". */
export function usedLabel(iso: string | null | undefined): string {
  const date = parse(iso);
  if (!date) return "never used";
  const now = new Date();
  if (sameDay(date, now)) return "used today";
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (sameDay(date, yesterday)) return "used yesterday";
  return `used ${formatDay(iso)}`;
}

/** "11 days ago", "Today", "Just now". */
export function agoLabel(iso: string | null | undefined): string {
  const date = parse(iso);
  if (!date) return "";
  const mins = Math.floor((Date.now() - date.getTime()) / 60_000);
  if (mins < 2) return "Just now";
  if (sameDay(date, new Date())) return "Today";
  const days = Math.max(1, Math.round(mins / 1440));
  return days === 1 ? "1 day ago" : `${days} days ago`;
}

/** "1 November" — the first day of the next period. */
export function formatResetDay(iso: string | null | undefined): string {
  const date = parse(iso);
  if (!date) return "";
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "long" });
}

export function monthName(iso: string | null | undefined): string {
  const date = parse(iso) ?? new Date();
  return date.toLocaleDateString("en-GB", { month: "long" });
}

/** "$12", "$12.40" — whole dollars stay whole. */
export function formatUsd(n: number): string {
  const rounded = Math.round(n * 100) / 100;
  return Number.isInteger(rounded) ? `$${rounded}` : `$${rounded.toFixed(2)}`;
}

export function plural(n: number, one: string, many = `${one}s`): string {
  return `${n} ${n === 1 ? one : many}`;
}

/** "a, b and c". */
export function listJoin(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} and ${items[items.length - 1]}`;
}
